import { LngLatBounds, Map, Marker } from 'maplibre-gl';
import { AllGeoJSON, centroid } from '@turf/turf';
import { renderToString } from 'react-dom/server';
import { Frame, ScoutLocation } from '@/types/location';
import { POLYGON_SOURCE_ID } from '@/components/ui/map/sources/DemoLocationsData';

const POLYGON_LAYER_ID = 'hm-demo-polygon-layer';
const STROKE_LAYER_ID = 'hm-demo-stroke-layer';

export function addLayers(map: Map) {
  const SEGMENT_LINE_WIDTH = 7;
  const SEGMENT_OUTLINE_WIDTH = 5;
  if (!map.getLayer(POLYGON_LAYER_ID)) {
    map.addLayer({
      id: POLYGON_LAYER_ID,
      source: POLYGON_SOURCE_ID,
      type: 'line',
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
      paint: {
        'line-color': 'white',
        'line-width': SEGMENT_LINE_WIDTH,
      },
    });
  }

  if (!map.getLayer(STROKE_LAYER_ID)) {
    map.addLayer(
      {
        id: STROKE_LAYER_ID,
        source: POLYGON_SOURCE_ID,
        type: 'line',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': 'black',
          'line-width': SEGMENT_OUTLINE_WIDTH + SEGMENT_LINE_WIDTH,
        },
      },
      POLYGON_LAYER_ID,
    );
  }
}

export function removeLayers(map: Map): void {
  if (map.getLayer(POLYGON_LAYER_ID)) {
    map.removeLayer(POLYGON_LAYER_ID);
  }
  if (map.getLayer(STROKE_LAYER_ID)) {
    map.removeLayer(STROKE_LAYER_ID);
  }
}

export function addLocationMarkers({
  map,
  locations,
  onLocationClick,
}: {
  map: Map;
  locations: ScoutLocation[];
  onLocationClick: (location: ScoutLocation) => void;
}): Marker[] {
  const markers = locations
    .map(location =>
      createLocationNameMarker({ location, onLocationClick, map }),
    )
    .filter(marker => marker) as Marker[];

  markers.forEach(marker => {
    marker.addTo(map);
  });

  return markers;
}

function createLocationNameMarker({
  location,
  onLocationClick,
  map,
}: {
  location: ScoutLocation;
  onLocationClick: (location: ScoutLocation) => void;
  map: Map;
}): Marker | undefined {
  const { geometry } = centroid(location.geojson as AllGeoJSON);
  const coords = geometry.coordinates as [number, number];

  // We create the elemtn as JSX first so TW linting (validation & ordering) works.
  const parent = document.createElement('div');
  parent.innerHTML = renderToString(
    <div
      data-zoom={map.getZoom()}
      className="group flex h-[48px] min-w-[48px] cursor-pointer items-center overflow-hidden rounded-full bg-neutral-1000 font-lato text-base transition-[max-width] duration-300 hover:z-[1000] hover:h-[48px] hover:w-auto hover:max-w-[500px]">
      <img
        className="ml-px min-h-[45px] min-w-[45px] rotate-90"
        src="/icons/bee-icon-colorful.png"
        width="45px"
        height="45px"
        alt="Bee Logo"
      />
      <div className="max-w-0 overflow-hidden text-ellipsis text-nowrap text-base font-bold transition-[max-width] duration-300 group-hover:ml-1 group-hover:mr-4 group-hover:max-w-[500px]">
        {location.name}
      </div>
    </div>,
  );

  const elem = parent.firstElementChild as HTMLElement;
  if (!elem) {
    console.error('Failed to create marker element', location);
    return undefined;
  }

  const marker = new Marker({ element: elem, anchor: 'left' }).setLngLat(
    coords,
  );

  elem.addEventListener('click', e => {
    if (!location.segments?.coordinates) return;

    const bounds = new LngLatBounds();
    for (const segment of location.segments.coordinates) {
      for (const coords of segment) {
        bounds.extend(coords as [number, number]);
      }
    }
    marker._map.fitBounds(bounds, {
      padding: {
        top: 365,
        left: 100,
        right: 100,
        bottom: 100,
      },
      speed: 2,
    });

    onLocationClick(location);
  });

  return marker;
}

export function addBeeIconMarker(map: Map, activeFrame: Frame): Marker {
  const markerEl = document.createElement('div');
  markerEl.style.display = 'flex';
  markerEl.innerHTML = renderToString(
    <img
      src="/icons/bee-icon-colorful.png"
      alt="bee marker"
      className="size-9"
    />,
  );

  const marker = new Marker({
    element: markerEl,
    draggable: false,
  });
  updateBeeIconMarker(map, marker, activeFrame);
  marker.addTo(map);

  return marker;
}

export function updateBeeIconMarker(
  map: Map,
  marker: Marker,
  activeFrame: Frame,
) {
  const { position, bearing } = activeFrame;
  marker.setLngLat({ lon: position.lon, lat: position.lat });
  if (bearing) {
    marker.setRotation(bearing - map.getBearing());
  }
}
